import { useState, useEffect, useRef, useContext } from 'react'

import AuthContext from '../../../context/AuthContext'
import { Messages } from '../../Message'
import {
  SEND_MESSAGE,
  USER_TYPING,
  UPDATE_USER_TYPING,
} from '../../../utils/sockets'

const LOCAL_TYPING_DEBOUNCE_DELAY = 1000 * 8
const REMOTE_TYPING_RESET_DELAY = 1000 * 12

const ViewUploadMessages = (props) => {
  const { upload, setUpload, socket, isDisconnected, isSubdealerUpload } = props

  const [auth] = useContext(AuthContext)

  const [currentMessage, setCurrentMessage] = useState('')
  const [isMessageLoading, setIsMessageLoading] = useState(false)

  const remoteTypingTimerId = useRef(null)
  const [localTyping, setLocalTyping] = useState(false)
  const [remoteTyping, setRemoteTyping] = useState({
    isTyping: false,
    user: null,
    isAdmin: null,
  })

  const updateUserTyping = (text) => {
    if (!isSubdealerUpload) {
      if (!localTyping && text) {
        setLocalTyping(true)
        return socket.emit(UPDATE_USER_TYPING, true)
      }

      if (!text) {
        setLocalTyping(false)
        return socket.emit(UPDATE_USER_TYPING, false)
      }
    }
  }

  const handleOnMessageSend = () => {
    if (!isSubdealerUpload) {
      const clientMessageId = `client-message-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`
      const messageToSend = currentMessage
      const optimisticMessage = {
        id: clientMessageId,
        clientMessageId,
        message: messageToSend,
        createdBy: auth.membership,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }

      setLocalTyping(false)
      setIsMessageLoading(true)
      setCurrentMessage('')

      setUpload((prevUpload) => ({
        messages: [...prevUpload.messages, optimisticMessage],
      }))

      socket.emit(UPDATE_USER_TYPING, false)

      socket.emit(SEND_MESSAGE, messageToSend, clientMessageId, (err, message) => {
        if (message) {
          setUpload((prevUpload) => {
            const confirmedExists = prevUpload.messages.some((existingMessage) => {
              return (
                existingMessage.id === message.id ||
                (message.clientMessageId &&
                  existingMessage.clientMessageId === message.clientMessageId &&
                  existingMessage.id !== clientMessageId &&
                  existingMessage.id !== message.id)
              )
            })

            const hasOptimisticMessage = prevUpload.messages.some(
              (existingMessage) => existingMessage.id === clientMessageId,
            )

            return {
              messages: hasOptimisticMessage
                ? prevUpload.messages.flatMap((existingMessage) => {
                    if (existingMessage.id === clientMessageId) {
                      return confirmedExists ? [] : [message]
                    }

                    return [existingMessage]
                  })
                : prevUpload.messages,
            }
          })
          setIsMessageLoading(false)
          return
        }

        setUpload((prevUpload) => ({
          messages: prevUpload.messages.filter(
            (existingMessage) =>
              existingMessage.clientMessageId !== clientMessageId,
          ),
        }))
        setCurrentMessage(messageToSend)
        setIsMessageLoading(false)
      })
    }
  }

  useEffect(() => {
    if (socket) {
      socket.on(USER_TYPING, ({ isTyping, user, isAdmin }) => {
        if (user && user.id === auth.membership.user.id) return

        if (remoteTypingTimerId.current) {
          clearTimeout(remoteTypingTimerId.current)
        }

        setRemoteTyping({ isTyping, user, isAdmin })

        if (isTyping) {
          const timerId = setTimeout(
            () =>
              setRemoteTyping({ isTyping: false, user: null, isAdmin: null }),
            REMOTE_TYPING_RESET_DELAY,
          )

          remoteTypingTimerId.current = timerId
        }
      })

      return () => socket.off(USER_TYPING)
    }
  }, [socket, auth.membership])

  useEffect(() => {
    if (localTyping) {
      const timerId = setTimeout(
        () => setLocalTyping(false),
        LOCAL_TYPING_DEBOUNCE_DELAY,
      )
      return () => clearTimeout(timerId)
    }
  }, [localTyping])

  return (
    <Messages
      currentMessage={currentMessage}
      setCurrentMessage={setCurrentMessage}
      messages={upload.messages}
      onMessageSend={handleOnMessageSend}
      isMessageLoading={isMessageLoading}
      isDisconnected={isDisconnected}
      remoteTyping={remoteTyping}
      onChangeText={updateUserTyping}
      isSupport={false}
      socket={socket}
      onNewFiles={(files) => setUpload({ files })}
      isDisabled={isSubdealerUpload}
    />
  )
}

export default ViewUploadMessages
